import dateUtils from "@/utils/date";
import { newlineToBr } from "@/utils/text-transformation";
import { MODES_DE_COMMANDE } from "@/utils/mode-commande";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";

export const planningProps = () => ({
  planning: {
    type: Array,
    required: false,
    default: () => []
  },
  creneauxFullWidth: {
    type: Boolean,
    required: false,
    default: false
  },
  infosLegales: {
    type: String,
    required: false
  },
  selectable: {
    type: Boolean,
    required: false,
    default: true
  },
  service: {
    type: Object,
    required: false,
    default: null
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false
  },
  isChoixSacsRequired: {
    type: Boolean,
    required: false,
    default: false
  },
  produitsIndispos: {
    type: Array,
    required: false,
    default: () => []
  },
  produitsIndisposOnly: {
    type: Boolean,
    required: false,
    default: false
  },
  sacsDriveIsError: {
    type: Boolean,
    required: false,
    default: false
  }
})

export const planningEmits = () => ([
  "dateSelected",
  "creneauSelected",
  "momentJourneeSelected",
  "creneauValidated",
  "deleteProduitsIndispos",
  "setSacDriveError"
])

export const usePlanning = (props, context) => {
  const selectedDate = ref(null)
  const selectedCreneau = ref(null)
  const momentJournee = ref(null)
  const cgu = ref(false)
  const pdp = ref(false)
  const displayMomentJournee = ref(true)

  const { trackEvent } = useInteractionDoneTracking();

  const parapharmacieAmount = computed(() => {
    const amount = props.produitsIndispos.filter(
      (article) => article.attributes.produit.data.attributes.is_parapharmacie
    ).length;

      return amount
  });

  const hasParapharmacie = computed(() => {
    return parapharmacieAmount.value > 0;
  })

  const hasOnlyParapharmacie = computed(() => {
    return parapharmacieAmount.value === props.produitsIndispos.length;
  })

  const isSunday = computed(() => {
    if (!selectedDate.value) {
      return false;
    }
    return dateUtils.isSunday(selectedDate.value.attributes.date_du_jour);
  })
  const firstDates = computed(() => {
    return props.planning.slice(0, 2);
  })
  const dropDownDates = computed(() => {
    return props.planning.slice(2);
  })
  const isDropDownSelected = computed(() => {
    if (!selectedDate.value) {
      return false;
    }
    const inList = dropDownDates.value.find(
      (date) => date.id === selectedDate.value.id
    );
    return !!inList;
  })
  const planningByMoment = computed(() => {
    const planningByMoment = {
      matin: [],
      aprem: [],
      soir: []
    };

    if (!selectedDate.value) {
      return planningByMoment;
    }

    for (const creneau of selectedDate.value.attributes.creneaux) {
      const moment = getMomentJournee(creneau.heure_debut);
      planningByMoment[moment].push(creneau);
    }
    return planningByMoment;
  })
  const infosLegalesFormatted = computed(() => {
    if (props.infosLegales) {
      return newlineToBr(props.infosLegales);
    }
    return "";
  })
  const slotButtonTag = computed(() => {
    return props.selectable ? "button" : "div";
  })
  const hasCreneauDispo = computed(() => {
    if (!selectedDate.value) {
      return false;
    }
    return selectedDate.value.attributes.creneaux.some(creneau => creneau.etat === true)

  })
  const uniqueMomentJournee = computed(() => {
    let alreadyFound = false;
    let response = null;
    for (const key in planningByMoment.value) {
      const found = Boolean(planningByMoment.value[key].length);
      if (found) {
        if (alreadyFound) {
          return null;
        }
        response = key;
        alreadyFound = true;
      }
    }
    return response;
  })

  const getDatesIndispo = (produit) => {
    return getJoursIndispo(produit.attributes.produit.data.attributes.jours_retraits)
  }

  const dateSelected = (date) => {
    displayMomentJournee.value = true;
    selectedCreneau.value = null;
    momentJournee.value = null;
    selectedDate.value = date;
    if (uniqueMomentJournee.value) {
      displayMomentJournee.value = false;

      setMomentJournee(uniqueMomentJournee.value);
    } else if (date && date.attributes && date.attributes.creneaux && date.attributes.creneaux.length > 0) {
      setMomentJournee(getMomentJournee(date.attributes.creneaux[0].heure_debut));
    }
    context.emit("dateSelected", date);
  }
  const creneauSelected = (creneau) => {
    if (props.selectable) {
      selectedCreneau.value = creneau;
      context.emit("creneauSelected", creneau);
    }
  }
  const isDateSelected = (dateId) => {
    return selectedDate.value && selectedDate.value.id === dateId;
  }
  const isCreneauSelected = (creneau) => {
    return (
      selectedCreneau.value &&
      selectedCreneau.value.heure_debut === creneau.heure_debut &&
      selectedCreneau.value.heure_fin === creneau.heure_fin
    );
  }
  const getMomentJournee = (heure) => {
    const h = parseInt(heure.slice(0, 2));
    if (h <= 12) {
      return "matin";
    }
    if (h > 12 && h <= 18) {
      return "aprem";
    }
    return "soir";
  }
  const setMomentJournee = (mj) => {
    momentJournee.value = mj;
    selectedCreneau.value = null;
    context.emit("momentJourneeSelected", mj);
  }
  const creneauValidated = () => {
    if (props.isChoixSacsRequired) {
      context.emit("setSacDriveError");
      return;
    }
    context.emit("creneauValidated", {
      date_du_jour: selectedDate.value.attributes.date_du_jour,
      heure_debut: selectedCreneau.value.heure_debut,
      heure_fin: selectedCreneau.value.heure_fin
    });
  }

  const reset = () => {
    selectedCreneau.value = null;
    selectedDate.value = null;
    momentJournee.value = null;
  }

  const changeCreneau = () => {
    trackEvent({
      categoryGA: trackingCodes.CATEGORYGA.checkoutPlanningPft,
      actionGA: trackingCodes.ACTIONGA.changerDeCreneau,
      labelGA: ""
    });
    window.scrollTo(0, 0);
    reset();
  }

  const deleteProduitsIndispos = () => {
    trackEvent({
      categoryGA: trackingCodes.CATEGORYGA.checkoutPlanningPft,
      actionGA: trackingCodes.ACTIONGA.supprimerMesProduits,
      labelGA: ""
    });
    context.emit("deleteProduitsIndispos");
  }

  watch(() => props.planning, () => {
    reset();
    if (firstDates.value && firstDates.value.length > 0) {
      dateSelected(firstDates.value[0]);
    }
  })

  return {
    hasOnlyParapharmacie,
    hasParapharmacie,
    selectedDate,
    selectedCreneau,
    momentJournee,
    cgu,
    pdp,
    displayMomentJournee,
    isSunday,
    firstDates,
    dropDownDates,
    isDropDownSelected,
    planningByMoment,
    infosLegalesFormatted,
    slotButtonTag,
    uniqueMomentJournee,
    hasCreneauDispo,
    dateSelected,
    creneauSelected,
    isDateSelected,
    isCreneauSelected,
    getMomentJournee,
    setMomentJournee,
    creneauValidated,
    reset,
    changeCreneau,
    deleteProduitsIndispos,
    getDatesIndispo,
    MODES_DE_COMMANDE
  }
}